"use client";

import { X, Video } from "lucide-react";
import { useUpcomingMeetings, UpcomingBot } from "@/hooks/useUpcomingMeetings";
import { BrandIcon } from "@/components/tailwind/ui/BrandIcon";
import { PLATFORM_CONNECTOR_IDS } from "@/home/utils";
import { formatInstant } from "@/lib/timezone";

const PLATFORM_COLORS: Record<string, string> = {
  google_meet: "bg-green-500",
  zoom: "bg-blue-500",
  teams: "bg-purple-500",
};

function timeLabel(minutesUntilStart: number): string {
  if (minutesUntilStart === 0) return "Now";
  if (minutesUntilStart === 1) return "in 1 minute";
  return `in ${minutesUntilStart} minutes`;
}

function BannerItem({ bot, onDismiss }: { bot: UpcomingBot; onDismiss: () => void }) {
  const connectorId = PLATFORM_CONNECTOR_IDS[bot.platform];
  const dotColor = PLATFORM_COLORS[bot.platform] ?? "bg-gray-400";
  const isNow = bot.minutesUntilStart === 0;

  return (
    <div className="flex items-center gap-3 bg-[#1c1c1e] dark:bg-[#1c1c1e] border border-[#3a3a3a] rounded-xl px-4 py-2.5 shadow-2xl min-w-[320px] max-w-[480px]">
      {/* Platform icon */}
      <div className="shrink-0 w-8 h-8 rounded-lg bg-[#2c2c2e] flex items-center justify-center">
        {connectorId ? (
          <BrandIcon id={connectorId} size={20} alt={bot.platform} />
        ) : (
          <Video className="w-4 h-4 text-gray-400" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {bot.title || bot.botName || "Upcoming meeting"}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isNow ? "bg-red-400 animate-pulse" : dotColor}`} />
          <p className="text-xs text-gray-400">
            {isNow ? "Starting now" : timeLabel(bot.minutesUntilStart)}
            {" · "}
            {formatInstant(bot.scheduledTime, {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
      </div>

      {/* Join button */}
      <a
        href={bot.meetingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-500 text-white text-xs font-semibold transition-colors"
      >
        Join and transcribe
      </a>

      {/* Dismiss */}
      <button
        onClick={onDismiss}
        className="shrink-0 p-1 rounded-md hover-component text-gray-500 hover:text-gray-300 transition-colors"
        title="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function MeetingReminderBanner() {
  const { upcoming, dismiss } = useUpcomingMeetings();

  if (upcoming.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[500] flex flex-col gap-2 items-center">
      {upcoming.map((bot) => (
        <BannerItem key={bot._id} bot={bot} onDismiss={() => dismiss(bot._id)} />
      ))}
    </div>
  );
}
